import React, { useEffect, useMemo, useState } from "react";
import { FiSearch, FiSliders, FiX } from "react-icons/fi";
import SellerCard from "./SellerCard";
import { apiRequest } from "../../api";
import { SERVICE_ICONS } from "../../data/serviceIcons";
import { cardClass } from "./jobUtils";

const inputClass = "px-4 py-2.5 rounded-lg bg-white/5 border border-navy-border text-white text-sm focus:outline-none focus:border-accent-light";
const emptyFilters = { service: "", country: "", min_rating: "", max_rate: "", sort: "rating" };

// The "Find Seller" search shown inside the Find Seller window: free-text search plus a filter panel
// (service, country, rating, rate, sort) that starts open. Messaging a seller needs a buyer account.
export default function SellerSearch({ settings, onMessage }) {
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState(emptyFilters);
  const [sellers, setSellers] = useState(null);
  const currency = settings?.currency_label || "USD";
  const services = useMemo(() => [...SERVICE_ICONS].sort((a, b) => a.label.localeCompare(b.label)), []);

  useEffect(() => {
    const timer = setTimeout(async () => {
      const params = new URLSearchParams();
      if (query.trim()) params.set("q", query.trim());
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const { ok, data } = await apiRequest(`/core/cowork/sellers/?${params}`);
      setSellers(ok ? data : []);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, filters]);

  const set = (field) => (e) => setFilters({ ...filters, [field]: e.target.value });
  const activeFilters = Object.entries(filters).filter(([k, v]) => v && !(k === "sort" && v === "rating")).length;
  const clear = () => { setFilters(emptyFilters); setQuery(""); };

  return (
    <div>
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, skill or service - e.g. logo, WordPress, translation"
            className={`${inputClass} w-full pl-9`}
          />
        </div>
        <button
          onClick={() => setFiltersOpen(!filtersOpen)}
          className={`px-4 py-2.5 rounded-lg border text-sm font-semibold inline-flex items-center gap-2 transition-colors ${
            filtersOpen || activeFilters ? "border-accent-light text-accent-light bg-accent/10" : "border-navy-border text-gray-300 hover:border-accent-light"
          }`}
        >
          <FiSliders /> Filters{activeFilters > 0 && <span className="w-5 h-5 rounded-full bg-accent-light text-navy-darker text-xs font-bold flex items-center justify-center">{activeFilters}</span>}
        </button>
      </div>

      {filtersOpen && (
        <div className={`${cardClass} mt-4 p-5`}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <label className="block">
              <span className="block text-xs text-gray-400 mb-1">Service</span>
              <select className={`${inputClass} w-full bg-navy-dark`} value={filters.service} onChange={set("service")}>
                <option value="">Any service</option>
                {services.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs text-gray-400 mb-1">Country</span>
              <input className={`${inputClass} w-full`} value={filters.country} onChange={set("country")} placeholder="Any country" />
            </label>
            <label className="block">
              <span className="block text-xs text-gray-400 mb-1">Minimum rating</span>
              <select className={`${inputClass} w-full bg-navy-dark`} value={filters.min_rating} onChange={set("min_rating")}>
                <option value="">Any rating</option>
                <option value="3">3 stars &amp; up</option>
                <option value="4">4 stars &amp; up</option>
                <option value="4.5">4.5 stars &amp; up</option>
              </select>
            </label>
            <label className="block">
              <span className="block text-xs text-gray-400 mb-1">Max hourly rate ({currency})</span>
              <input type="number" min="0" className={`${inputClass} w-full`} value={filters.max_rate} onChange={set("max_rate")} placeholder="No limit" />
            </label>
          </div>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
            <label className="block">
              <span className="block text-xs text-gray-400 mb-1">Sort by</span>
              <select className={`${inputClass} bg-navy-dark`} value={filters.sort} onChange={set("sort")}>
                <option value="rating">Best rated</option>
                <option value="jobs">Most jobs done</option>
                <option value="rate_low">Lowest rate</option>
                <option value="newest">Newest</option>
              </select>
            </label>
            {(activeFilters > 0 || query) && (
              <button onClick={clear} className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1"><FiX /> Clear all</button>
            )}
          </div>
        </div>
      )}

      <div className="mt-6">
        {sellers === null ? (
          <p className="text-center text-gray-500 py-10">Searching...</p>
        ) : sellers.length === 0 ? (
          <div className={`${cardClass} text-center py-12 px-6`}>
            <p className="text-lg font-semibold">No sellers match your search</p>
            <p className="text-gray-400 mt-1">Try a different keyword or remove a filter.</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-400 mb-4">{sellers.length} seller{sellers.length === 1 ? "" : "s"} found</p>
            <div className="grid sm:grid-cols-2 gap-5">
              {sellers.map((seller, i) => <SellerCard key={seller.id} seller={seller} currency={currency} onMessage={onMessage} index={i} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
